import {
  useState,
  ReactElement,
  ReactNode,
  ReactPortal,
  JSXElementConstructor,
} from "react";

import {
  Alert,
  Button,
  Container,
  Nav,
  Navbar,
  NavDropdown,
} from "react-bootstrap";
import ReactTooltip from "react-tooltip";

import { BUILDINGS } from "@/modules/building/routes";
import { isWithDefaultGroup } from "@/modules/dwelling/containers/DwellingEstimateContainer/utils/utils.ts";
import { DWELLINGS } from "@/modules/dwelling/routes";
import { MANAGEMENT } from "@/modules/profile/routes/ProfileRoutes";
import ServiceOrderingMenu from "@/modules/serviceOrdering/components/ServiceOrderingMenu";
import ServiceOrderingShoppingCart from "@/modules/serviceOrdering/components/ServiceOrderingShoppingCart";
import { store } from "@/store/index.ts";

import DwellLogo from "./DwellLogo";
import ModalCGU from "./ModalCGU";
import ModalPolicy from "./ModalPolicy";
import Version from "./Version.tsx";
import DwellIcon from "../../assets/svg/DwellIcon.tsx";
import DwellIconSm from "../../assets/svg/DwellIconSm.tsx";
import { useServiceInventoryStore } from "../../modules/serviceOrdering/store/ServiceInventoryStore.ts";
import { NEW_ORDER } from "../../routes/app";
import history from "../../routes/history";

const LayoutComponent: ({
  logOut,
  isLogged,
  user,
  children,
}: {
  logOut: any;
  isLogged: any;
  user: any;
  children: any;
}) =>
  | ReactElement<any, string | JSXElementConstructor<any>>
  | string
  | number
  | {}
  | Iterable<ReactNode>
  | ReactPortal
  | boolean
  | null
  | undefined = ({ logOut, isLogged, user, children }) => {
  const [isOpen, setIsOpen] = useState();
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showCGUModal, setShowCGUModal] = useState(false);
  const [error, setError] = useState("");
  const businessServiceType = useServiceInventoryStore(
    (state) => state.businessServiceType
  );

  const logOutFunc = () => {
    logOut()
      .then(() => history.push("/login"))
      .catch(() => setError("Erreur lors de la deconnexion"));
  };

  const { authReducer } = store.getState();

  const logoUrl = authReducer?.user?.groupInfo?.themeLogoUrl;

  // to display infotheque depending if the user is in légimetrie
  const isDefaultGroup = isWithDefaultGroup(user);
  // console.log("isDefaultGroup", isDefaultGroup);

  if (!isLogged) {
    return children;
  }

  return (
    <div className="layout">
      <div
        className={
          isOpen
            ? "sidebar-close d-flex flex-column align-content-start justify-content-center flex-wrap d-sm-block d-none"
            : "sidebar d-flex flex-column align-content-start justify-content-center flex-wrap d-sm-block d-none"
        }
      >
        <div className="d-lg-block d-none ">
          {!isOpen ? (
            <>
              <div className="d-flex m-2 justify-content-center">
                <DwellLogo linkTo="/" logoUrl={logoUrl} />
              </div>
              <Button
                variant="none"
                className="mx-2 d-flex fw-bolder"
                onClick={() => setIsOpen(!isOpen)}
              >
                <i className="bi bi-chevron-left me-lg-3" />
                Menu
              </Button>
            </>
          ) : (
            <>
              <div className=" d-flex m-2 justify-content-center">
                <DwellLogo size="sm" linkTo="/" logoUrl={logoUrl} />
              </div>
              <Button variant="none" onClick={() => setIsOpen(!isOpen)}>
                <i
                  className="bi bi-chevron-right mx-3"
                  onClick={() => setIsOpen(!isOpen)}
                />
              </Button>
            </>
          )}
        </div>
        <Nav
          className="d-flex justify-content-between pt-5"
          activeKey={history.location.pathname}
        >
          <Nav.Link
            active={history.location.pathname === "/"}
            onClick={() => history.push("/")}
            className="mb-2 mx-2 d-flex align-items-center"
            data-tip="Tableau de bord"
          >
            <i className={isOpen ? "bi bi-grid" : "bi bi-grid me-lg-3"}></i>
            <p
              className={
                isOpen
                  ? "d-sm-none m-0 navbar-side-menu"
                  : "d-lg-block d-none m-0 navbar-side-menu"
              }
            >
              Tableau de bord
            </p>
          </Nav.Link>
          <Nav.Link
            active={history.location.pathname === DWELLINGS}
            onClick={() => history.push(DWELLINGS)}
            className="mb-2 mx-2 d-flex align-items-center"
            data-tip="Mes biens"
          >
            <i
              className={
                isOpen ? "bi bi-house-door" : "bi bi-house-door me-lg-3"
              }
            ></i>
            <p
              className={
                isOpen
                  ? "d-sm-none m-0 navbar-side-menu"
                  : "d-lg-block d-none m-0 navbar-side-menu"
              }
            >
              Mes biens
            </p>
          </Nav.Link>
          <Nav.Link
            active={history.location.pathname === BUILDINGS}
            onClick={() => history.push(BUILDINGS)}
            className="mb-2 mx-2 d-flex align-items-center"
            data-tip="Mes immeubles"
          >
            <i
              className={isOpen ? "bi bi-building" : "bi bi-building me-lg-3"}
            ></i>
            <p
              className={
                isOpen
                  ? "d-sm-none m-0 navbar-side-menu"
                  : "d-lg-block d-none m-0 navbar-side-menu"
              }
            >
              Mes immeubles
            </p>
          </Nav.Link>
          <Nav.Link
            active={history.location.pathname === "/my-orders"}
            onClick={() => history.push("/my-orders")}
            className="mb-2 mx-2 d-flex align-items-center"
            data-tip="Mes commandes"
          >
            <i className={isOpen ? "bi bi-cart2" : "bi bi-cart2 me-lg-3"}></i>
            <p
              className={
                isOpen
                  ? "d-sm-none m-0 navbar-side-menu"
                  : "d-lg-block d-none m-0 navbar-side-menu"
              }
            >
              Mes commandes
            </p>
          </Nav.Link>
          {isDefaultGroup && (
            <Nav.Link
              active={history.location.pathname === "/infotheque"}
              onClick={() => history.push("/infotheque")}
              className="mb-2 mx-2 d-flex align-items-center"
              data-tip="Infothèque"
            >
              <i
                className={
                  isOpen ? "bi bi-info-circle" : "bi bi-info-circle me-lg-3"
                }
              ></i>
              <p
                className={
                  isOpen
                    ? "d-sm-none m-0 navbar-side-menu"
                    : "d-lg-block d-none m-0 navbar-side-menu"
                }
              >
                Infothèque
              </p>
            </Nav.Link>
          )}

          <Nav.Link
            href="/login"
            className="mb-2 mx-2 d-flex align-items-center"
            data-tip="Déconnexion"
            onClick={logOutFunc}
          >
            <i className={isOpen ? "bi bi-power" : "bi bi-power me-lg-3"}></i>
            <p
              className={
                isOpen
                  ? "d-sm-none m-0 navbar-side-menu"
                  : "d-lg-block d-none m-0 navbar-side-menu"
              }
            >
              Déconnexion
            </p>
          </Nav.Link>
          {error !== "" && (
            <Alert className="mt-2 w-100 mx-2" variant="danger">
              {error}
            </Alert>
          )}

          <NavDropdown
            drop="end"
            title={
              <>
                <i
                  className={
                    isOpen
                      ? "bi bi-info-circle"
                      : "bi bi-file-earmark-text me-lg-3"
                  }
                ></i>
                <p
                  className={
                    isOpen ? "d-sm-none  m-0" : "d-lg-inline d-none m-0"
                  }
                >
                  Mentions Légales
                </p>
              </>
            }
            className=" mb-2 mx-2  justify-content-end d-sm-flex d-lg-none"
            id="basic-nav-dropdown"
          >
            <NavDropdown.Item>
              <span className="link register-policies">
                <strong onClick={() => setShowCGUModal(true)}>CGV / CGU</strong>
              </span>
            </NavDropdown.Item>
            <NavDropdown.Item>
              <span className="link register-policies">
                <strong onClick={() => setShowPolicyModal(true)}>
                  Politique de Confidentialité
                </strong>
              </span>
            </NavDropdown.Item>
          </NavDropdown>

          {isOpen !== true ? (
            <div className="d-none d-lg-flex flex-column gap-2 position-absolute bottom-0 ms-4 pb-3">
              <div className="d-flex m-2 justify-content-center">
                <DwellIcon />
              </div>
              <span className="link register-policies">
                <strong onClick={() => setShowCGUModal(true)}>CGV / CGU</strong>
              </span>
              <span className="mb-2 link register-policies">
                <strong onClick={() => setShowPolicyModal(true)}>
                  Politique de Confidentialité
                </strong>
              </span>
              <Version />
            </div>
          ) : (
            <>
              <NavDropdown
                drop="end"
                data-tip="Mentions légales"
                title={
                  <>
                    <i className="bi bi-file-earmark-text me-lg-3 "></i>
                    <p
                      className={
                        isOpen
                          ? "d-lg-none  m-0"
                          : "d-lg-block d-none m-0 navbar-side-menu"
                      }
                    >
                      Mentions Légales
                    </p>
                  </>
                }
                className=" mb-2 mx-2  justify-content-end d-md-none d-lg-flex"
                id="basic-nav-dropdown"
              >
                <NavDropdown.Item>
                  <span className="link register-policies">
                    <strong onClick={() => setShowCGUModal(true)}>
                      CGV / CGU
                    </strong>
                  </span>
                </NavDropdown.Item>
                <NavDropdown.Item>
                  <span className="link register-policies">
                    <strong onClick={() => setShowPolicyModal(true)}>
                      Politique de Confidentialité
                    </strong>
                  </span>
                </NavDropdown.Item>
              </NavDropdown>
              <div className="d-none d-lg-flex flex-column position-absolute bottom-0 ms-2 mb-4 pb-5">
                <div className="d-flex  justify-content-center">
                  <DwellIconSm />
                </div>
              </div>
            </>
          )}
          {isOpen ? (
            <ReactTooltip
              place="right"
              type="light"
              effect="solid"
              className="tooltip-text"
            />
          ) : (
            <></>
          )}
        </Nav>
      </div>
      <div className="layout_right">
        <Navbar
          collapseOnSelect
          expand="lg"
          bg="light"
          variant="light"
          className={`${
            isOpen ? "navbar-close" : ""
          } dire shadow-sm bg-body flex-column justify-content-center`}
        >
          <Container fluid className="my-2">
            <Navbar.Toggle
              className="justify-content-end text-decoration-none"
              aria-controls="responsive-navbar-nav"
            />
            <div
              className="d-flex align-items-center d-block d-lg-none"
              id="collasible-nav-dropdown"
            >
              <img
                src={
                  "https://placehold.co/40x40/000D5B/FFF/png?text=" +
                  user?.firstname?.charAt(0)
                }
                alt=""
                className="navbar-user-icon"
              />
              <NavDropdown
                className="d-flex align-items-center navbar-user-menu"
                align={"end"}
                title={
                  <>
                    {user.firstname} {user.lastname}{" "}
                    <i className="bi bi-chevron-down ms-2"></i>
                  </>
                }
              >
                <NavDropdown.Item href="/profile">
                  <i className="bi bi-person me-2" />
                  Mon compte
                </NavDropdown.Item>
                <NavDropdown.Item href="/group">
                  <i className="bi bi-globe2 me-2" />
                  Mon groupe
                </NavDropdown.Item>
                <NavDropdown.Item onClick={() => history.push(MANAGEMENT)}>
                  <i className="bi bi-building me-2" />
                  Gestion
                </NavDropdown.Item>
                <NavDropdown.Item href="/support">
                  <i className="bi bi-headset me-2" />
                  Support
                </NavDropdown.Item>
              </NavDropdown>
            </div>
            <Navbar.Collapse
              id="responsive-navbar-nav"
              className="flex-nowrap justify-content-between gap-4"
            >
              <Nav className="ms-lg-3  flex-grow-1"></Nav>
              <div className="d-flex align-content-start flex-wrap d-block d-sm-none">
                <Nav
                  className="align-items-start w-100 align-items-center "
                  activeKey={history.location.pathname}
                >
                  <Nav.Link
                    active={history.location.pathname === "/"}
                    onClick={() => history.push("/")}
                    className="w-100 align-items-center ps-2 mb-2 d-flex"
                  >
                    <i className="bi bi-grid me-3"></i>
                    <p className="m-0">Tableau de bord</p>
                  </Nav.Link>
                  <Nav.Link
                    active={history.location.pathname === DWELLINGS}
                    onClick={() => history.push(DWELLINGS)}
                    className="w-100 align-items-center ps-2 mb-2 d-flex"
                  >
                    <i className="bi bi-house-door me-3"></i>
                    <p className="m-0">Mes biens</p>
                  </Nav.Link>
                  <Nav.Link
                    active={history.location.pathname === BUILDINGS}
                    onClick={() => history.push(BUILDINGS)}
                    className="w-100 align-items-center ps-2 mb-2 d-flex"
                  >
                    <i className="bi bi-building me-3"></i>
                    <p className="m-0">Mes immeubles</p>
                  </Nav.Link>
                  <Nav.Link
                    active={history.location.pathname === "/my-orders"}
                    onClick={() => history.push("/my-orders")}
                    className="w-100 align-items-center ps-2 mb-2 d-flex"
                  >
                    <i className="bi bi-cart2 me-3"></i>
                    <p className="m-0">Mes commandes</p>
                  </Nav.Link>
                  <Nav.Link
                    active={history.location.pathname === "/infotheque"}
                    onClick={() => history.push("/infotheque")}
                    className="w-100 align-items-center ps-2 mb-2 d-flex"
                  >
                    <i className="bi bi-info-circle me-3"></i>
                    <p className="m-0">Infothèque</p>
                  </Nav.Link>
                  <Nav.Link
                    onClick={logOutFunc}
                    className="w-100 align-items-center ps-2 mb-2 d-flex"
                  >
                    <i className="bi bi-power me-3"></i>
                    <p className="m-0">Déconnexion</p>
                  </Nav.Link>
                  {error !== "" && (
                    <Alert className="mt-2 w-100 mx-2" variant="danger">
                      {error}
                    </Alert>
                  )}

                  <Nav.Link className="w-100 align-items-center ps-2 mb-2 d-flex">
                    <i className="bi bi-file-earmark-text me-3"></i>
                    <p className="m-0" onClick={() => setShowCGUModal(true)}>
                      CGV / CGU
                    </p>
                  </Nav.Link>
                  <Nav.Link className="w-100 align-items-center ps-2 mb-2 d-flex">
                    <i className="bi bi-file-earmark-medical me-3"></i>
                    <p className="m-0" onClick={() => setShowPolicyModal(true)}>
                      Politique de Confidentialité
                    </p>
                  </Nav.Link>
                </Nav>
              </div>
              <Nav className="me-lg-5">
                <Button
                  className="navbar-agenda-button"
                  variant="success"
                  role="button"
                  onClick={() => history.push("/agenda")}
                >
                  <i className="bi bi-calendar4-week me-2"></i>
                  Agenda
                </Button>
                <Button
                  className="navbar-new-order-button"
                  variant="primary"
                  role="button"
                  onClick={() => history.push("/new-order")}
                >
                  <i className="bi bi-cart2 me-2"></i>
                  Nouvelle commande
                </Button>
                <div
                  className="d-lg-flex d-none align-items-center"
                  id="collasible-nav-dropdown"
                >
                  <img
                    src={
                      "https://placehold.co/40x40/000D5B/FFF/png?text=" +
                      user?.firstname?.charAt(0)
                    }
                    alt=""
                    className="navbar-user-icon"
                  />
                  <NavDropdown
                    className="d-flex align-items-center"
                    align={"end"}
                    title={
                      <>
                        {user.firstname} {user.lastname}{" "}
                        <i className="bi bi-chevron-down ms-2"></i>
                      </>
                    }
                  >
                    <NavDropdown.Item onClick={() => history.push("/profile")}>
                      <i className="bi bi-person me-2" />
                      Mon compte
                    </NavDropdown.Item>
                    <NavDropdown.Item onClick={() => history.push(MANAGEMENT)}>
                      <i className="bi bi-building me-2" />
                      Gestion
                    </NavDropdown.Item>
                  </NavDropdown>
                </div>
              </Nav>
            </Navbar.Collapse>
          </Container>
          {history.location.pathname === NEW_ORDER && (
            <>
              {businessServiceType && (
                <div className="w-100 px-4 pb-2">
                  <div className="border-bottom my-3" />
                  <div className="d-flex justify-content-between flex-wrap">
                    <ServiceOrderingMenu />
                    <ServiceOrderingShoppingCart />
                  </div>
                </div>
              )}
            </>
          )}
        </Navbar>
        <div className="layout_content p-4">{children}</div>
      </div>
      <ModalCGU show={showCGUModal} setShow={setShowCGUModal} />
      <ModalPolicy show={showPolicyModal} setShow={setShowPolicyModal} />
    </div>
  );
};

export default LayoutComponent;
